import { Platform } from 'react-native';
import { useToken, Box } from '@gluestack-ui/themed';

/**
 * High-tech dot grid background with animated glowing spots.
 * Only rendered on Web platforms to provide a modern, immersive feel.
 *
 * @returns Animated background elements or null on mobile
 */
export const AnimatedBackground = () => {
  // Resolve colors. If primaryColor starts with $, we resolve it.
  // @ts-expect-error - brandStrong is a dynamic token
  const themePrimary = useToken('colors', 'brandStrong');
  // @ts-expect-error - brandSubtle is a dynamic token
  const themeSubtle = useToken('colors', 'brandSubtle');

  if (Platform.OS !== 'web') return null;

  // Ensure we have valid CSS colors.
  // If useToken is called before the provider is ready or if tokens aren't resolved yet,
  // it might return the token name itself (e.g., 'brandStrong').
  // CSS radial-gradient requires actual color values.
  const isColor = (val: string) => val && (val.startsWith('#') || val.startsWith('rgba') || val.startsWith('rgb'));
  const baseColor = isColor(themePrimary) ? themePrimary : '#A9D14C';
  const subtleColor = isColor(themeSubtle) ? themeSubtle : '#D7EDAB';

  return (
    <Box
      position="absolute"
      top={0}
      bottom={0}
      left={0}
      right={0}
      overflow="hidden"
      pointerEvents="none"
      bg="$white"
    >
      {/* CSS with drifting keyframes for blobs */}
      <style>
        {`
        @keyframes loginDrift1 {
          0% { transform: translate(0, 0) scale(1); }
          20% { transform: translate(150px, -100px) scale(1.3); }
          40% { transform: translate(50px, 120px) scale(0.8); }
          60% { transform: translate(-120px, 60px) scale(1.2); }
          80% { transform: translate(-80px, -80px) scale(0.9); }
          100% { transform: translate(0, 0) scale(1); }
        }
        @keyframes loginDrift2 {
          0% { transform: translate(0, 0) scale(1); }
          25% { transform: translate(-180px, 80px) scale(1.4); }
          50% { transform: translate(100px, -60px) scale(0.7); }
          75% { transform: translate(60px, 140px) scale(1.1); }
          100% { transform: translate(0, 0) scale(1); }
        }
        @keyframes loginDrift3 {
          0% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(100px, -120px) scale(1.5); }
          66% { transform: translate(-80px, 80px) scale(0.6); }
          100% { transform: translate(0, 0) scale(1); }
        }
        .login-blob-1 {
          animation: loginDrift1 15s ease-in-out infinite !important;
        }
        .login-blob-2 {
          animation: loginDrift2 20s ease-in-out infinite !important;
        }
        .login-blob-3 {
          animation: loginDrift3 12s ease-in-out infinite !important;
        }
      `}
      </style>

      {/* Dot grid pattern - more subtle */}
      <Box
        position="absolute"
        top={0}
        bottom={0}
        left={0}
        right={0}
        opacity={0.5}
        sx={{
          _web: {
            backgroundImage: `radial-gradient(circle, ${subtleColor} 1.5px, transparent 1.5px)`,
            backgroundSize: '32px 32px',
          },
        }}
      />

      {/* Large animated blobs - increased opacity and adjusted blur for visibility */}
      <Box
        // @ts-expect-error - className is used for web animations
        className="login-blob-1"
        position="absolute"
        top="-5%"
        right="-5%"
        w={800}
        h={800}
        borderRadius="$full"
        bg={baseColor}
        opacity={0.25}
        sx={{
          _web: {
            filter: 'blur(80px)',
          },
        }}
      />

      <Box
        // @ts-expect-error - className is used for web animations
        className="login-blob-2"
        position="absolute"
        bottom="-10%"
        left="-10%"
        w={700}
        h={700}
        borderRadius="$full"
        bg={baseColor}
        opacity={0.2}
        sx={{
          _web: {
            filter: 'blur(80px)',
          },
        }}
      />

      <Box
        // @ts-expect-error - className is used for web animations
        className="login-blob-3"
        position="absolute"
        top="30%"
        right="5%"
        w={500}
        h={500}
        borderRadius="$full"
        bg={subtleColor}
        opacity={0.15}
        sx={{
          _web: {
            filter: 'blur(60px)',
          },
        }}
      />

      {/* Grounding vignette - lighter to let colors through */}
      <Box
        position="absolute"
        top={0}
        bottom={0}
        left={0}
        right={0}
        pointerEvents="none"
        sx={{
          _web: {
            background:
              'radial-gradient(ellipse at center, transparent 40%, rgba(255,255,255,0.2) 70%, rgba(255,255,255,0.5) 100%)',
          },
        }}
      />
    </Box>
  );
};
